import {
    Component,
    Input,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslateDirective, TranslatePipe } from "@ngx-translate/core";
import _ from "lodash";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { DirtyTabComponent } from "~app/features/regelverk-viol/dirtyTabComponent";
import { JlpKundorderSearchModel } from "../../common/models/jlp-kundorder.model";
import { IJlpResultSettings } from "../../common/models/jlp-model";
import { GridPeriod, IOrderKundorderSearchResponse } from "../../common/models/justeraLeveransplanModel";
import { JlpPeriodService } from "../../common/services/jlp-period.service";
import { JusteraLeveransplanFilterService } from "../../common/services/justera-leveransplan-filter.service";
import { JusteraLeveransplanService } from "../../common/services/justera-leveransplan.service";
import { KundorderValidation } from "../../common/validation/jlp-validation";
import { JlpSavedSearchesComponent } from "../../components/jlp-saved-searches/jlp-saved-searches.component";
import { JlpKundorderResultComponent } from "./jlp-kundorder-result/jlp-kundorder-result.component";

@Component({
    selector: "rask-jlp-kundorder",
    templateUrl: "./jlp-kundorder.component.html",
    styleUrls: ["./jlp-kundorder.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        JlpSavedSearchesComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent,
        TreeSelectorComponent,
        DatepickerComponent,
        ButtonComponent,
        JlpKundorderResultComponent,
        TranslateDirective,
        TranslatePipe
    ]
})

export class JlpKundorderComponent extends DirtyTabComponent implements OnInit {

    readonly WIKSparade = "JusteraLeveransplan.kundorderfilter.sparade";
    readonly name = "kundorder";

    basdata: any = null;
    @Input() canEdit: boolean;
    currentData: IOrderKundorderSearchResponse;
    currentPeriod: IAggregatPeriod = null;
    kundorderForm = new UntypedFormGroup({
        kunder: new UntypedFormControl([]),
        omraden: new UntypedFormControl([]),
        mottagningsplatser: new UntypedFormControl([]),
        handelssortimentsgrupper: new UntypedFormControl([]),
        periodFrom: new UntypedFormControl(),
        periodTom: new UntypedFormControl()
    });
    model: JlpKundorderSearchModel;
    noOrderrows = false;
    period: GridPeriod;
    @ViewChild(JlpKundorderResultComponent) results: JlpKundorderResultComponent;
    settings: IJlpResultSettings;
    showResults = false;
    triggerSparaFilter: any;
    validationContext: ValidationContext;

    private readonly WIK = "JusteraLeveransplan.kundorderfilter";
    private readonly busyService = inject(BusyService);
    private readonly dirtyFormServiceRef: DirtyFormService;
    private readonly formBinder = inject(FormBinderService);
    private readonly jlpPeriodService = inject(JlpPeriodService);
    private readonly jlpService = inject(JusteraLeveransplanService);
    private readonly justeraLeveransplanFilterService = inject(JusteraLeveransplanFilterService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        const dirtyFormService = inject(DirtyFormService);

        super(dirtyFormService);
        this.dirtyFormServiceRef = dirtyFormService;
    }

    canSave() {
        this.results.isDirty();
    }

    formatter = (result: ITypeAheadResult) => result ? result.namn : undefined;

    isDirtyCheck(): boolean {
        if (this.results === undefined) {
            return false;
        }
        return this.results.isDirty();
    }

    ngOnInit() {
        this.jlpService.getJusteraLeveransplanBasdata().subscribe((basdata: any) => {
            this.model = _.merge(new JlpKundorderSearchModel(), this.webinstallningarService.get<JlpKundorderSearchModel>(this.WIK));
            this.basdata = this.justeraLeveransplanFilterService.setFilter(basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
            this.basdata = this.justeraLeveransplanFilterService.setFilter(basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.produktionsomrade);
            this.setForm();
            this.validationContext = KundorderValidation.init(this.model);
        });
    }

    outputFormatter = (result: ITypeAheadResult) => result ? [result] : undefined;

    reset() {
        this.kundorderForm.controls.kunder.setValue([]);
        this.kundorderForm.controls.omraden.setValue([]);
        this.kundorderForm.controls.mottagningsplatser.setValue([]);
        this.kundorderForm.controls.handelssortimentsgrupper.setValue([]);
        this.kundorderForm.controls.periodFrom.setValue(null);
        this.kundorderForm.controls.periodTom.setValue(null);
        this.setModel(this.kundorderForm.value);

        this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
        this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.produktionsomrade);
        this.setForm();
    }

    restoreChanges() {
        this.search();
    }

    saveChanges() {
        this.results.save();
    }

    saveSearchParams() {
        this.kundorderForm.value.handelssortimentsgrupper = this.justeraLeveransplanFilterService.getSelectedHandelssortiment(this.basdata.handelssortiment);
        this.kundorderForm.value.omraden = this.justeraLeveransplanFilterService
            .getSelectedAreas(this.basdata.omraden, this.justeraLeveransplanFilterService.areaType.marknadsomrade);

        if (ObjectUtils.hasValue(this.kundorderForm.get("periodFrom").value)) {
            this.kundorderForm.value.periodFrom = this.jlpPeriodService.addFirstDayOfMonth(this.kundorderForm.get("periodFrom").value);
        }

        if (ObjectUtils.hasValue(this.kundorderForm.get("periodTom").value)) {
            this.kundorderForm.value.periodTom = this.jlpPeriodService.getLastDayOfMonth(this.kundorderForm.get("periodTom").value);
        }

        this.triggerSparaFilter = Object.assign({}, this.kundorderForm.value);
    }

    search() {
        const searchFunc = () => {
            this.noOrderrows = false;
            this.currentData = null;
            this.setModel(this.kundorderForm.value);
            this.validationContext.scope(ValidationScopes.sok, ValidationErrorLevels.error).subscribe(scope => {
                if (this.validationContext.validate(true)) {
                    this.kundorderForm.value.handelssortimentsgrupper = this.justeraLeveransplanFilterService.getSelectedHandelssortiment(this.basdata.handelssortiment);
                    this.kundorderForm.value.omraden = this.justeraLeveransplanFilterService
                        .getSelectedAreas(this.basdata.omraden, this.justeraLeveransplanFilterService.areaType.marknadsomrade);

                    this.kundorderForm.value.periodFrom = this.jlpPeriodService.addFirstDayOfMonth(this.kundorderForm.get("periodFrom").value);
                    this.kundorderForm.value.periodTom = this.jlpPeriodService.getLastDayOfMonth(this.kundorderForm.get("periodTom").value);

                    this.webinstallningarService.set(this.WIK, this.kundorderForm.value);
                    const query = this.jlpService.searchForKundorder(this.kundorderForm.value);
                    this.busyService.addWork(query).subscribe((res: IOrderKundorderSearchResponse) => {
                        this.showResults = true;
                        if (res.kundorderrader.length === 0) {
                            this.noOrderrows = true;
                        } else {
                            this.settings = {
                                passeradeAntalArbetsdagarInnevarandeManad: res.passeradeAntalArbetsdagarInnevarandeManad,
                                totaltAntalArbetsdagarInnevarandeManad: res.totaltAntalArbetsdagarInnevarandeManad,
                                periodFrom: this.kundorderForm.get("periodFrom").value,
                                periodTom: this.kundorderForm.get("periodTom").value
                            };
                            this.period = this.jlpPeriodService.getPeriods(this.settings.periodFrom, this.settings.periodTom);
                            this.currentData = res;
                        }
                    });
                }
            });
        };

        if (this.isDirtyCheck()) {
            this.dirtyFormServiceRef.dirtyFormModal(
                () => searchFunc(),
                () => {
                    this.saveChanges();
                    searchFunc();
                },
                () => undefined);
        } else {
            searchFunc();
        }
    }

    searchForCustomer = (searchString: string) => this.jlpService.searchForKundTypeAhead(searchString);

    searchForMottagningsplatsTypeAhead = (searchString: string) => this.jlpService.searchForMottagningsplatsTypeAhead(searchString, null);

    setSokning(namedSokFilter: any) {
        this.reset();
        if (namedSokFilter) {
            this.setModel(namedSokFilter);
            this.basdata.omraden = null;
            this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.handelssortiment);
            this.basdata = this.justeraLeveransplanFilterService.setFilter(this.basdata, this.model, this.justeraLeveransplanFilterService.treeTypes.produktionsomrade);
            this.setForm();
        }
    }

    private setForm() {
        if (!ObjectUtils.hasValue(this.kundorderForm)) {
            this.kundorderForm = this.formBinder.CreateGroup(this.model);
        } else {
            this.kundorderForm.controls.kunder.setValue(this.model.kunder);
            this.kundorderForm.controls.mottagningsplatser.setValue(this.model.mottagningsplatser);
            this.kundorderForm.controls.periodFrom.setValue(this.model.periodFrom);
            this.kundorderForm.controls.periodTom.setValue(this.model.periodTom);
            this.kundorderForm.controls.handelssortimentsgrupper.setValue(this.model.handelssortimentsgrupper);
            this.kundorderForm.controls.omraden.setValue(this.model.omraden);
        }
    }

    private setModel(values: any) {
        this.model.kunder = values.kunder;
        this.model.mottagningsplatser = values.mottagningsplatser;
        this.model.periodFrom = values.periodFrom;
        this.model.periodTom = values.periodTom;
        this.model.omraden = values.omraden ?? [];
        this.model.handelssortimentsgrupper = values.handelssortimentsgrupper ?? [];
    }
}
